import { useQuery } from '@tanstack/react-query';
import { getAcademicTerms } from '../../services/calendarService';
import { getTracks } from '../../services/gradeTrackService';
import {
  getClassScheduleSettings,
  getProgramColorSettings,
  resolveGradeProgramColors,
} from '../../services/sectionLayoutService';
import { loadAnnualProgressDataCache } from '../../utils/annualProgressDataCache';
import { schoolKeys } from '../keys';
import { cacheOnlyQueryOptions } from './cacheOnlyQuery';
import { lsInitialDataOptions } from './lsInitialData';
import type { AnnualProgressMetaBundle } from './types';

export type UseAnnualProgressMetaQueryParams = {
  workspaceId: string;
  academicYear: number;
  termNumber: 1 | 2;
  gradeId: string;
  enabled?: boolean;
  /** true면 REST 없이 LS/RQ 캐시만 사용 (메인 프레젠 등) */
  cacheOnly?: boolean;
};

function metaFromAnnualProgressCache(
  workspaceId: string,
  academicYear: number,
  termNumber: 1 | 2
): { found: boolean; data: AnnualProgressMetaBundle } {
  const tryTerm = (tn: 1 | 2) => {
    const cached = loadAnnualProgressDataCache(workspaceId, academicYear, tn);
    if (!cached.found) return null;
    const { state } = cached;
    return {
      found: true as const,
      data: {
        terms: state.terms,
        schedule: state.schedule,
        tracks: state.tracks,
        regularColors: state.regularColors,
        dedicatedColors: state.dedicatedColors,
        baseExcludeColors: state.baseExcludeColors,
      },
    };
  };
  // terms/schedule은 학기 공통 — 요청 term miss면 다른 학기 aggregate LS로 폴백
  return (
    tryTerm(termNumber) ??
    tryTerm(termNumber === 1 ? 2 : 1) ?? {
      found: false,
      data: {
        terms: [],
        schedule: null,
        tracks: [],
        regularColors: [],
        dedicatedColors: [],
        baseExcludeColors: [],
      },
    }
  );
}

export function annualProgressMetaQueryOptions(params: UseAnnualProgressMetaQueryParams) {
  const { workspaceId, academicYear, termNumber, gradeId, enabled = true, cacheOnly = false } = params;
  const ls = metaFromAnnualProgressCache(workspaceId, academicYear, termNumber);

  return {
    queryKey: schoolKeys.annualProgressMeta(workspaceId, academicYear, termNumber, gradeId),
    enabled: enabled && !!workspaceId.trim() && !!gradeId.trim(),
    ...lsInitialDataOptions(ls.found, ls.data),
    ...cacheOnlyQueryOptions(cacheOnly),
    queryFn: async (): Promise<AnnualProgressMetaBundle> => {
      const [terms, schedule, tracks, colorSettings] = await Promise.all([
        getAcademicTerms(workspaceId, academicYear),
        getClassScheduleSettings(workspaceId),
        getTracks(workspaceId),
        getProgramColorSettings(workspaceId),
      ]);
      const rc = resolveGradeProgramColors(colorSettings, gradeId);
      return {
        terms,
        schedule,
        tracks,
        regularColors: rc.regular,
        dedicatedColors: rc.dedicated,
        baseExcludeColors: rc.baseExclude,
      };
    },
  };
}

export function useAnnualProgressMetaQuery(params: UseAnnualProgressMetaQueryParams) {
  return useQuery(annualProgressMetaQueryOptions(params));
}
